import { GlobalKeyValueService } from './GlobalKeyValueService';
import { getUserProfileValue, resolveApiUrl } from './URLUtils';

export interface UrlReplacementContext {
    programContainer?: any;
    appName?: string;
    customValues?: { [key: string]: string };
    record?: any;
}

export class DynamicUrlInterpreter {
    private static readonly PLACEHOLDER_REGEX = /\{\{([^}]+)\}\}/g;
    
    static interpretUrl(url: string, context: UrlReplacementContext = {}): string {
        if (!url) return '';
        
        let interpretedUrl = url;
        
        interpretedUrl = this.replaceGlobalKeyValues(interpretedUrl, context);
        interpretedUrl = this.replaceUserProfileValues(interpretedUrl);
        interpretedUrl = this.replaceSessionStorageValues(interpretedUrl);
        interpretedUrl = this.replaceFormFieldValues(interpretedUrl, context);
        interpretedUrl = this.replaceRecordValues(interpretedUrl, context);
        interpretedUrl = this.replaceCustomValues(interpretedUrl, context);
        
        interpretedUrl = resolveApiUrl(interpretedUrl, {
            programContainer: context.programContainer
        });
        
        return interpretedUrl;
    }
    
    private static replaceGlobalKeyValues(url: string, context: UrlReplacementContext): string {
        const appName = context.appName || context.programContainer?.CategoryId;
        if (!appName) return url;
        
        const globalPairs = GlobalKeyValueService.getKeyValuePairsWithUrlFallback(appName);
        let result = url;
        
        globalPairs.forEach(pair => {
            const placeholder = `{{${pair.key}}}`;
            const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            result = result.replace(regex, encodeURIComponent(pair.value));
        });
        
        return result;
    }
    
    private static replaceUserProfileValues(url: string): string {
        let result = url;
        
        const userFields = [
            'userId', 'USERID', 'id',
            'operatorNumber',
            'organizationId',
            'email',
            'firstName',
            'lastName',
            'displayName',
            'department',
            'role'
        ];
        
        userFields.forEach(field => {
            const value = getUserProfileValue(field);
            if (value) {
                const placeholders = [`{{${field}}}`, `{{${field.toUpperCase()}}}`, `{{${field.toLowerCase()}}}`];
                placeholders.forEach(placeholder => {
                    const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                    result = result.replace(regex, encodeURIComponent(String(value)));
                });
            }
        });
        
        return result;
    }
    
    private static replaceSessionStorageValues(url: string): string {
        let result = url;
        
        const sessionKeys = [
            'selectedService',
            'organizationId',
            'userId',
            'authToken',
            'currentStep',
            'formId'
        ];
        
        sessionKeys.forEach(key => {
            const value = sessionStorage.getItem(key);
            if (value) {
                let processedValue = value;
                
                if (key === 'selectedService') {
                    try {
                        const parsed = JSON.parse(value);
                        if (parsed.serviceId) {
                            const serviceIdPlaceholder = /\{\{serviceId\}\}/gi;
                            result = result.replace(serviceIdPlaceholder, encodeURIComponent(String(parsed.serviceId)));
                        }
                    } catch (e) {
                    
                    }
                } else {
                    const placeholder = `{{${key}}}`;
                    const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                    result = result.replace(regex, encodeURIComponent(processedValue));
                }
            }
        });
        
        return result;
    }
    
    private static replaceFormFieldValues(url: string, context: UrlReplacementContext): string {
        if (!context.programContainer) return url;
        
        let result = url;
        const placeholders: string[] = result.match(this.PLACEHOLDER_REGEX) || [];
        
        placeholders.forEach(placeholder => {
            const fieldId = placeholder.replace(/\{\{|\}\}/g, '');
            
            if (this.isStandardPlaceholder(fieldId)) {
                return;
            }
            
            const fieldValue = this.findFieldValueInContainer(context.programContainer, fieldId);
            if (fieldValue !== null && fieldValue !== undefined) {
                result = result.replace(placeholder, encodeURIComponent(String(fieldValue)));
            }
        });
        
        return result;
    }
    
    private static replaceRecordValues(url: string, context: UrlReplacementContext): string {
        if (!context.record) return url;
        
        let result = url;
        const placeholders: string[] = result.match(this.PLACEHOLDER_REGEX) || [];
        
        placeholders.forEach(placeholder => {
            const fieldName = placeholder.replace(/\{\{|\}\}/g, '');
            
            if (this.isStandardPlaceholder(fieldName)) {
                return;
            }
            
            const value = context.record[fieldName];
            if (value !== null && value !== undefined) {
                result = result.replace(placeholder, encodeURIComponent(String(value)));
            }
        });
        
        return result;
    }
    
    private static replaceCustomValues(url: string, context: UrlReplacementContext): string {
        if (!context.customValues) return url;
        
        let result = url;
        
        Object.entries(context.customValues).forEach(([key, value]) => {
            const placeholder = `{{${key}}}`;
            const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
            result = result.replace(regex, encodeURIComponent(String(value)));
        });
        
        return result;
    }
    
    private static isStandardPlaceholder(fieldId: string): boolean {
        const standardPlaceholders = [
            'page', 'pageIndex', 'size', 'pageSize', 'offset', 'limit',
            'sortField', 'sortBy', 'sortOrder', 'sortDirection',
            'operatorNumber', 'serviceId', 'userId', 'USERID', 'organizationId',
            'email', 'firstName', 'lastName', 'displayName', 'department', 'role'
        ];
        
        return standardPlaceholders.includes(fieldId.toLowerCase());
    }

    private static getValueByPath(source: any, path: string): any {
        if (!source || !path) return undefined;

        const segments = path.split('.').filter(Boolean);
        let current = source;

        for (const segment of segments) {
            if (current === null || current === undefined || typeof current !== 'object') {
                return undefined;
            }

            const direct = (current as any)[segment];
            if (direct !== undefined) {
                current = direct;
                continue;
            }

            const matchedKey = Object.keys(current).find(key => key.toLowerCase() === segment.toLowerCase());
            if (!matchedKey) {
                return undefined;
            }

            current = (current as any)[matchedKey];
        }

        return current;
    }
    
    private static findFieldValueInContainer(programContainer: any, fieldId: string): any {
        const entityValue = this.getValueByPath(programContainer?.Entity, fieldId);
        if (entityValue !== undefined && entityValue !== null && entityValue !== '') {
            return entityValue;
        }

        if (!programContainer?.ListOfFormSections) return null;

        const target = (fieldId || '').trim().toLowerCase();
        
        for (const section of programContainer.ListOfFormSections) {
            if (!section.ListOfFormSubSections) continue;
            
            for (const subSection of section.ListOfFormSubSections) {
                if (!subSection.ListOfFormSectionElements) continue;
                
                for (const element of subSection.ListOfFormSectionElements) {
                    const loadBindName = (element.LoadBindName || '').toString().trim().toLowerCase();
                    const elementId = (element.Id || '').toString().trim().toLowerCase();
                    if (loadBindName === target || elementId === target) {
                        const matchedValue = element.Value ?? element.SelectionCode;
                        if (matchedValue !== null && matchedValue !== undefined && matchedValue !== '') {
                            return matchedValue;
                        }
                    }
                    
                    if (element.subSectionList) {
                        for (const nestedSubSection of element.subSectionList) {
                            if (nestedSubSection.ListOfFormSectionElements) {
                                for (const nestedElement of nestedSubSection.ListOfFormSectionElements) {
                                    const nestedLoadBindName = (nestedElement.LoadBindName || '').toString().trim().toLowerCase();
                                    const nestedElementId = (nestedElement.Id || '').toString().trim().toLowerCase();
                                    if (nestedLoadBindName === target || nestedElementId === target) {
                                        const matchedValue = nestedElement.Value ?? nestedElement.SelectionCode;
                                        if (matchedValue !== null && matchedValue !== undefined && matchedValue !== '') {
                                            return matchedValue;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return null;
    }
    
    static getAvailablePlaceholders(context: UrlReplacementContext = {}): string[] {
        const placeholders: string[] = [];
        
        const appName = context.appName || context.programContainer?.CategoryId;
        if (appName) {
            const globalPairs = GlobalKeyValueService.getKeyValuePairs(appName);
            placeholders.push(...globalPairs.map(pair => `{{${pair.key}}}`));
        }
        
        placeholders.push(
            '{{userId}}', '{{operatorNumber}}', '{{organizationId}}',
            '{{email}}', '{{firstName}}', '{{lastName}}', '{{displayName}}'
        );
        
        placeholders.push('{{serviceId}}', '{{authToken}}');
        if (context.programContainer?.ListOfFormSections) {
            for (const section of context.programContainer.ListOfFormSections) {
                if (section.ListOfFormSubSections) {
                    for (const subSection of section.ListOfFormSubSections) {
                        if (subSection.ListOfFormSectionElements) {
                            for (const element of subSection.ListOfFormSectionElements) {
                                if (element.LoadBindName) {
                                    placeholders.push(`{{${element.LoadBindName}}}`);
                                }
                            }
                        }
                    }
                }
            }
        }
        
        if (context.record) {
            Object.keys(context.record).forEach(key => {
                placeholders.push(`{{${key}}}`);
            });
        }
        
        if (context.customValues) {
            Object.keys(context.customValues).forEach(key => {
                placeholders.push(`{{${key}}}`);
            });
        }
        
        return Array.from(new Set(placeholders)).sort();
    }
    
    static validateUrl(url: string, context: UrlReplacementContext = {}): {
        isValid: boolean;
        unresolvedPlaceholders: string[];
        resolvedUrl: string;
    } {
        const resolvedUrl = this.interpretUrl(url, context);
        const unresolvedPlaceholders = resolvedUrl.match(this.PLACEHOLDER_REGEX) || [];
        
        return {
            isValid: unresolvedPlaceholders.length === 0,
            unresolvedPlaceholders: unresolvedPlaceholders.map(p => p.replace(/\{\{|\}\}/g, '')),
            resolvedUrl
        };
    }
}
